import React, {
  useState,
  useEffect,
  useContext,
  lazy,
  Suspense,
  useMemo,
} from "react";
import Box from "@mui/material/Box";
import CustomTabs from "../CustomTabs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ProfileContext } from "../../contexts/ProfileContext";
import { CanEditPeopleProvider } from "../../contexts/CanEditPeopleContext";
import Loader from "../../Loader";
import { useSelector } from "react-redux";

const ResumesBlock = lazy(() => import("./Resume/ResumesBlock"));
const MainProfileInfos = lazy(() => import("./MainProfileInfos"));
const ExperiencesSection = lazy(() => import("./ExperiencesSection"));
const HobbiesSection = lazy(() => import("./HobbiesSection"));
const LicensesSection = lazy(() => import("./LicensesSection"));
const TestsSection = lazy(() => import("./TestsSection"));
const ProfileDocuments = lazy(() => import("./ProfileDocuments"));
const NewSkillsSection = lazy(
  () => import("../skills/newSkills/NewSkillsSection")
);
const AdditionalInfo = lazy(() => import("./additionalInfo/AdditionalInfo"));
const ProfileCustomTests = lazy(() => import("./ProfileCustomTests"));

const tabColors: { [key: string]: string } = {
  experiences: "error",
  trainings: "warning",
  skills: "info",
  aptitudes: "success",
  hobbies: "default",
  licenses: "primary",
  documents: "secondary",
};

const ProfileContent: React.FC<{
  employeeData?: any;
  testData?: any;
  notCollab: boolean;
  loading?: boolean;
  context: string;
  tabChange: string;
  candidateId: string;
  canEditPeople?: boolean;
}> = ({
  employeeData,
  notCollab,
  context,
  loading,
  testData,
  tabChange,
  candidateId,
  canEditPeople,
}) => {
  const paramCustomTest = useSelector((state: any) =>
    Boolean(state?.home?.paramjson?.customTest)
  );
  const hideTransversal = useSelector(
    (state: any) => state?.home?.paramjson?.hideTransversal
  );

  const cvCreator = useSelector((state: any) => {
    const b = state?.home?.paramjson?.cvCreator;
    return typeof b !== "boolean" ? true : Boolean(b);
  });

  const { customTests, tests } = useMemo(
    () => ({
      customTests:
        testData?.["hydra:member"]?.filter((el: any) => el.custom) ?? [],
      tests: testData?.["hydra:member"]?.filter((el: any) => !el.custom) ?? [],
    }),
    [testData]
  );

  const [tabs, setTabs] = useState([
    ...(notCollab ? ["tests"] : []),
    ...(paramCustomTest && customTests?.length > 0 ? ["customTests"] : []),
    "experiences",
    ...(cvCreator ? ["resume"] : []),
    "trainings",
    "skills",
    ...(hideTransversal ? [] : ["aptitudes"]),
    "additional_info",
    "hobbies",
    "licenses",
    "documents",
  ]);

  const params = useParams();
  const location = useLocation();

  const { peopleData, careerItems } = useContext(ProfileContext);

  const [redirect, setRedirect] = useState<any>(null);
  const [currentTab, setCurrentTab] = useState<any>(null);
  const navigate = useNavigate();
  const handleChange = (val: number, fromComponent?: boolean) => {
    const newTab = tabs[val];
    const currentTab = tabs[value];

    if (currentTab === newTab) return;

    navigate(location.pathname.replace(currentTab, newTab));

    fromComponent && setRedirect(val);
  };
  const [tabCount, setTabCount] = useState<{ [key: string]: number }>({});
  const [loadingTabCount, setLoadingTabCount] = useState(true);

  const isThird = context === "third_party";
  const anonymous = peopleData?.anonymous;

  const isCollab =
    context === "collab" || context === "candidate" || context === "benef";

  useEffect(() => {
    const countTrainings = careerItems?.filter(
      (el: any) => el.type === "training"
    ).length;

    const countExperiences = careerItems?.filter(
      (el: any) => el.type === "experience"
    ).length;

    setTabCount({
      experiences: countExperiences,
      trainings: countTrainings,
      skills: peopleData?.countTechnicalSkills,
      aptitudes: peopleData?.countTransversalSkills,
      hobbies: peopleData?.countHobbies,
      licenses: peopleData?.countDriverLicenses,
      documents: peopleData?.countFiles,
      resume: peopleData?.countResumes,
    });

    setLoadingTabCount(false);
  }, [
    careerItems,
    peopleData?.countDriverLicenses,
    peopleData?.countFiles,
    peopleData?.countHobbies,
    peopleData?.countTechnicalSkills,
    peopleData?.countTransversalSkills,
    peopleData?.countResumes,
  ]);

  useEffect(() => {
    setTabs((arr: any) => {
      if (
        paramCustomTest &&
        customTests?.length > 0 &&
        !arr.includes("customTests")
      ) {
        arr.splice(1, 0, "customTests");
      }
      if (
        (context === "candidate" || context === "recruiter") &&
        peopleData?.onboardingTest
      ) {
        if (!peopleData?.onboardingTest.includes("skills")) {
          arr = arr.filter(
            (el: string) => el !== "skills" && el !== "aptitudes"
          );
        }

        if (!peopleData?.onboardingTest.includes("job")) {
          arr = arr.filter(
            (el: string) => el !== "trainings" && el !== "experiences"
          );
        }
      }

      return arr;
    });
  }, [peopleData?.onboardingTest, context, customTests, paramCustomTest]);

  const displayTab = () => {
    switch (tabs[value]) {
      case "overview":
        return (
          <Suspense fallback={<Loader />}>
            <MainProfileInfos
              changeTab={(e: number) => handleChange(e, true)}
              notCollab={notCollab}
              context={context}
              tabs={tabs}
            />
          </Suspense>
        );
      case "experiences":
        return (
          <Suspense fallback={<Loader />}>
            <ExperiencesSection
              canEditPeople={canEditPeople}
              type="experience"
              notCollab={notCollab}
              {...(isCollab ? {} : { personId: peopleData.id })}
            />
          </Suspense>
        );
      case "trainings":
        return (
          <Suspense fallback={<Loader />}>
            <ExperiencesSection
              canEditPeople={canEditPeople}
              type="training"
              notCollab={notCollab}
              {...(isCollab ? {} : { personId: peopleData?.id })}
            />
          </Suspense>
        );
      case "skills":
        // return <SkillsSection type="skills" notCollab={notCollab} />;
        return (
          <Suspense fallback={<Loader />}>
            <NewSkillsSection
              {...(isCollab ? {} : { personId: peopleData?.id })}
            />
          </Suspense>
        );
      case "aptitudes":
        // return <SkillsSection type="transversal" notCollab={notCollab} />;
        return (
          <Suspense fallback={<Loader />}>
            <NewSkillsSection
              {...(isCollab ? {} : { personId: peopleData?.id })}
              transversal
            />
          </Suspense>
        );
      case "tests":
        return (
          <Suspense fallback={<Loader />}>
            <TestsSection
              notCollab={notCollab}
              data={{
                ...testData,
                "hydra:member": tests,
              }}
              loading={loading}
              anonymous={anonymous}
            />
          </Suspense>
        );
      case "hobbies":
        return (
          <Suspense fallback={<Loader />}>
            <HobbiesSection
              notCollab={notCollab}
              employeeData={employeeData}
              personId={peopleData?.id}
            />
          </Suspense>
        );
      case "licenses":
        return (
          <Suspense fallback={<Loader />}>
            <LicensesSection
              notCollab={notCollab}
              employeeData={employeeData}
              personId={peopleData?.id}
            />
          </Suspense>
        );
      case "documents":
        return (
          <Suspense fallback={<Loader />}>
            <ProfileDocuments
              person={peopleData?.id ?? employeeData?.id}
              notCollab={notCollab}
            />
          </Suspense>
        );
      case "resume":
        return (
          <Suspense fallback={<Loader />}>
            <ResumesBlock
              person={peopleData?.id ?? employeeData?.id}
              notCollab={notCollab}
              canEditPeople={canEditPeople}
            />
          </Suspense>
        );
      case "additional_info":
        return (
          <Suspense fallback={<Loader />}>
            <AdditionalInfo notCollab={notCollab} personId={peopleData?.id} />
          </Suspense>
        );
      case "customTests":
        return (
          <Suspense fallback={<Loader />}>
            <ProfileCustomTests tests={customTests} />
          </Suspense>
        );
      default:
        return (
          <Box>
            <p>{tabs[value]}</p>
          </Box>
        );
    }
  };

  const value = useMemo(() => {
    const index = tabs.findIndex((el: string) => el === params.tab);
    return index < 0 ? 0 : index;
  }, [params.tab, tabs]);

  useEffect(() => {
    setCurrentTab(displayTab());

    const url = isThird
      ? `/app/profile/${params.profileId}/${params.id}/${tabs[value]}`
      : `/app/profile/${employeeData ? `${employeeData.id}/` : ""}${
          tabs[value]
        }${candidateId ? `?candidateId=${candidateId}` : ""}`;

    navigate(url, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <CanEditPeopleProvider>
      <Box>
        <Box display="grid">
          <CustomTabs
            value={value}
            onChange={(e: number, force: boolean) => {
              handleChange(force ? e : redirect);
            }}
            tabs={tabs.map((tab: string) => `profilepage.${tab}`)}
            tabChange={tabChange}
            chip={true}
            chipNumber={tabs.map((tab: string) => tabCount[tab])}
            chipColors={tabs.map((tab: string) => tabColors[tab])}
            loading={loadingTabCount}
          />
        </Box>
        <Box>{currentTab}</Box>
      </Box>
    </CanEditPeopleProvider>
  );
};

export default ProfileContent;
